import React, { useContext } from 'react';
import { styled } from '@mui/material';
import classNames from 'classnames';
import {
  diffDay2,
  getDate,
  getDay,
  getWeekDayText2,
  weekNo,
} from '../../../Utils/dateUtils';
import EDTypography from '../../Typography/EDTypography';
import {
  getDayNameShortText,
  getDayNameText,
  isRedDay,
} from '../../../Utils/dayNames';
import { firstUpper } from '../../../Utils/ramdaCookbook';
import EDBox from '../../EDBox/EDBox';
import { ViewDbSettingsContext } from '../../../Features/Delsystem/Bokning/Rutiner/Kalender/Components/SchedulerComponent/ViewDbSettingsContext';

interface IProps {
  day: Date;
  dayWidth: number;
  /** If true renders week number instead of day name */
  showWeekNumber?: boolean;
  isSmallScreen?: boolean;
  textAlign?: 'left' | 'center';
  /** If true, adds space at bottom for long bookings */
  allDayBookingExists?: boolean;
  /** Number of long bookings to calculate space for */
  allDayBookingCount?: number;
}

export const EDCalendarViewDayCell: React.FC<IProps> = props => {
  const viewDbSettingsContext = useContext(ViewDbSettingsContext);
  const redDay = isRedDay(props.day);
  const today = diffDay2(props.day, getDate()) === 0;
  const dayNameText = firstUpper(
    props.dayWidth >= 200
      ? getDayNameText(props.day)
      : getDayNameShortText(props.day)
  );

  const bottomPadding = props.allDayBookingExists
    ? `${(props.allDayBookingCount || 1) * 25}px`
    : '0px';

  const getCorrectView = (view: string) => {
    if (view === 'WeekOverview' || view === 'WeekOverview2') {
      return 'DayOverview';
    } else {
      return 'Day2';
    }
  };

  return (
    <DayContainer
      onClick={() => {
        viewDbSettingsContext.setCurrentDate(props.day);
        viewDbSettingsContext.setCurrentView(
          getCorrectView(viewDbSettingsContext.currentView)
        );
      }}
      className={classNames({
        [`${props.textAlign}`]: props.textAlign,
      })}
      style={{ paddingBottom: bottomPadding }}
    >
      <WeekdayTypography
        className={classNames({
          ['redDay']: redDay,
          ['today']: today,
        })}
      >
        {getWeekDayText2(
          props.day,
          props.isSmallScreen ? true : props.dayWidth < 80
        )}
      </WeekdayTypography>
      <DayNumberTypography
        className={classNames({
          ['redDay']: redDay,
          ['today']: today,
          ['isSmallScreen']: props.isSmallScreen,
        })}
      >
        {getDay(props.day)}
      </DayNumberTypography>
      {props.showWeekNumber ? (
        <WeekNumberTypography>v {weekNo(props.day)}</WeekNumberTypography>
      ) : (
        <DayNameTypography
          className={classNames({
            ['redDay']: redDay,
            ['hidden']: !dayNameText,
          })}
        >
          {!props.isSmallScreen && dayNameText}
        </DayNameTypography>
      )}
    </DayContainer>
  );
};

const DayContainer = styled(EDBox, { name: 'DayContainer' })(({ theme }) => ({
  display: 'inline-block',
  cursor: 'pointer',
  padding: `0 ${theme.spacing(3)}`,
  '&.left': {
    textAlign: 'left',
    paddingTop: '7px',
    marginBottom: 'auto',
  },
  '&.center': {
    textAlign: 'center',
  },
}));

const WeekdayTypography = styled(EDTypography, { name: 'WeekdayTypography' })(
  ({ theme }) => ({
    fontSize: 12,
    textTransform: 'lowercase',
    '&.redDay': {
      color: 'red',
    },
    '&.today': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  })
);

const DayNumberTypography = styled(EDTypography, {
  name: 'DayNumberTypography',
})(({ theme }) => ({
  fontSize: theme.spacing(3.5),
  '&.redDay': {
    color: 'red',
  },
  '&.today': {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  '&.isSmallScreen': {
    fontSize: theme.spacing(2),
  },
}));

const DayNameTypography = styled(EDTypography, { name: 'DayNameTypography' })({
  fontSize: 12,
  '&.redDay': {
    color: 'red',
  },
  '&.hidden': {
    visibility: 'hidden',
  },
});

const WeekNumberTypography = styled(EDTypography, {
  name: 'WeekNumberTypography',
})(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.primary,
}));
